import React, { useEffect, useRef, useState } from "react";
import {
    Card, Button, Tag, Descriptions, Modal, message,
    Statistic, Row, Col, Spin, Input, Space, Progress,
} from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaperPlane, faPen, faRotateRight } from "@fortawesome/free-solid-svg-icons";
import { format } from "date-fns";
import emailCampaignApi from "../../../api/emailCampaignApi";
import EmailCampaignLogsTable from "./email-campaign-logs-table";

const STATUS_TAG = {
    draft: { color: "blue", label: "Nháp" },
    sending: { color: "orange", label: "Đang gửi" },
    done: { color: "green", label: "Hoàn thành" },
    failed: { color: "red", label: "Thất bại" },
};

/** Hiển thị đối tượng nhận dạng dễ đọc */
function AudienceLabel({ payload }) {
    const ap = (typeof payload === "string" ? (() => { try { return JSON.parse(payload); } catch { return {}; } })() : payload) || {};

    if (ap.mode === "all") return <span>Tất cả users có email</span>;

    if (ap.mode === "ids") {
        const ids = ap.userIds || [];
        return (
            <span>
                {ids.length.toLocaleString()} user cụ thể
                {ids.length <= 20 && <span className="text-content-3 ml-1">({ids.join(", ")})</span>}
            </span>
        );
    }

    if (ap.mode === "filtered") {
        const f = ap.filters || {};
        const labels = [];
        if (f.verified !== undefined) labels.push(f.verified ? "Đã xác thực" : "Chưa xác thực");
        if (f.active !== undefined) labels.push(f.active ? "Đang hoạt động" : "Bị khoá");
        if (f.role) labels.push(`Vai trò: ${f.role}`);
        if (f.isLiveUser !== undefined) labels.push(f.isLiveUser ? "Live User" : "Không phải Live User");
        if (f.isBuffUser !== undefined) labels.push(f.isBuffUser ? "Buff User" : "Không phải Buff User");
        if (f.minCash !== undefined) labels.push(`Cash ≥ ${Number(f.minCash).toLocaleString()}đ`);
        if (f.maxCash !== undefined) labels.push(`Cash ≤ ${Number(f.maxCash).toLocaleString()}đ`);
        if (f.minRank !== undefined) labels.push(`Rank ≥ ${f.minRank}`);
        if (f.maxRank !== undefined) labels.push(`Rank ≤ ${f.maxRank}`);
        return labels.length ? (
            <Space size={4} wrap>{labels.map((l, i) => <Tag key={i}>{l}</Tag>)}</Space>
        ) : <span>Bộ lọc (không có điều kiện)</span>;
    }

    return <span className="text-content-3">-</span>;
}

function EmailCampaignDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [campaign, setCampaign] = useState(null);
    const [stats, setStats] = useState({ queued: 0, sent: 0, failed: 0 });
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [resuming, setResuming] = useState(false);
    const [testEmail, setTestEmail] = useState("");
    const [testLoading, setTestLoading] = useState(false);
    const [previewTab, setPreviewTab] = useState("preview");
    const intervalRef = useRef(null);

    const fetchDetail = async () => {
        try {
            const { data } = await emailCampaignApi.getCampaign(id);
            setCampaign(data.data.campaign);
            setStats(data.data.stats);
            return data.data.campaign;
        } catch {
            message.error("Không thể tải chi tiết chiến dịch");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDetail().then((c) => {
            if (c?.status === "sending") {
                intervalRef.current = setInterval(async () => {
                    const updated = await fetchDetail();
                    if (updated?.status !== "sending") clearInterval(intervalRef.current);
                }, 5000);
            }
        });
        return () => clearInterval(intervalRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handleTestSend = async () => {
        if (!testEmail) { message.warning("Nhập email nhận thử"); return; }
        if (!campaign?.subject) return;
        try {
            setTestLoading(true);
            await emailCampaignApi.testSend({
                toEmail: testEmail,
                subject: campaign.subject,
                ...(campaign.htmlBody ? { htmlBody: campaign.htmlBody } : { templateName: campaign.templateName }),
            });
            message.success(`Đã gửi thử tới ${testEmail}`);
        } catch (err) {
            message.error(err?.response?.data?.message || "Gửi thử thất bại");
        } finally {
            setTestLoading(false);
        }
    };

    const handleResume = () => {
        Modal.confirm({
            title: "Resume chiến dịch",
            content: `Re-enqueue ${stats.queued.toLocaleString()} email còn đang chờ?`,
            okText: "Resume",
            cancelText: "Hủy",
            onOk: async () => {
                try {
                    setResuming(true);
                    const { data } = await emailCampaignApi.resumeCampaign(id);
                    message.success(`Đã re-enqueue ${data.data.requeuedJobs} email`);
                    intervalRef.current = setInterval(async () => {
                        const c = await fetchDetail();
                        if (c?.status !== "sending") clearInterval(intervalRef.current);
                    }, 5000);
                } catch (err) {
                    message.error(err?.response?.data?.message || "Resume thất bại");
                } finally {
                    setResuming(false);
                }
            },
        });
    };

    const handleSend = () => {
        Modal.confirm({
            title: "Xác nhận gửi chiến dịch",
            content: `Gửi email "${campaign?.name}" đến tất cả users phù hợp?`,
            okText: "Gửi ngay",
            okType: "danger",
            cancelText: "Hủy",
            onOk: async () => {
                try {
                    setSending(true);
                    await emailCampaignApi.sendCampaign(id);
                    message.success("Đã bắt đầu gửi chiến dịch");
                    const updated = await fetchDetail();
                    if (updated?.status === "sending") {
                        intervalRef.current = setInterval(async () => {
                            const c = await fetchDetail();
                            if (c?.status !== "sending") clearInterval(intervalRef.current);
                        }, 5000);
                    }
                } catch (err) {
                    message.error(err?.response?.data?.message || "Gửi thất bại");
                } finally {
                    setSending(false);
                }
            },
        });
    };

    if (loading) {
        return <div className="flex-1 flex items-center justify-center"><Spin size="large" /></div>;
    }
    if (!campaign) return null;

    const statusTag = STATUS_TAG[campaign.status] || { color: "default", label: campaign.status };
    const total = campaign.targetUserCount || 0;
    const done = stats.sent + stats.failed;
    const progressPct = total > 0 ? Math.round((done / total) * 100) : 0;
    const hasBeenSent = campaign.status !== "draft";

    return (
        <div className="flex-1 p-6 overflow-auto max-w-4xl">
            {/* Header */}
            <div className="flex items-center gap-3 mb-6 flex-wrap">
                <Button onClick={() => navigate("/email")}>← Quay lại</Button>
                <h1 className="text-xl font-semibold m-0">{campaign.name}</h1>
                <Tag color={statusTag.color}>{statusTag.label}</Tag>
                {campaign.status === "draft" && (
                    <Button
                        icon={<FontAwesomeIcon icon={faPen} className="mr-1" />}
                        onClick={() => navigate(`/email/${id}/edit`)}
                    >
                        Sửa
                    </Button>
                )}
                {campaign.status === "draft" && (
                    <Button
                        type="primary"
                        danger
                        icon={<FontAwesomeIcon icon={faPaperPlane} className="mr-1" />}
                        loading={sending}
                        onClick={handleSend}
                    >
                        Gửi ngay
                    </Button>
                )}
                {/* Resume chỉ khi queue đã trống mà log vẫn 'queued' (job thật sự mất).
                    Còn job trong queue = đang gửi bình thường; Resume lúc đó gửi trùng. */}
                {campaign.status === "sending" && stats.queued > 0 && stats.pendingJobs > 0 && (
                    <span className="text-gray-500">Đang gửi… còn {stats.pendingJobs.toLocaleString()} email trong hàng đợi</span>
                )}
                {campaign.status === "sending" && stats.queued > 0 && stats.pendingJobs === 0 && (
                    <Button
                        type="primary"
                        icon={<FontAwesomeIcon icon={faRotateRight} className="mr-1" />}
                        loading={resuming}
                        onClick={handleResume}
                    >
                        Resume ({stats.queued.toLocaleString()} email)
                    </Button>
                )}
            </div>

            {/* Thống kê */}
            <Card className="mb-4">
                <Row gutter={16} className="mb-3">
                    <Col span={6}>
                        <Statistic title="Mục tiêu" value={total || "-"} />
                    </Col>
                    <Col span={6}>
                        <Statistic title="Đã gửi" value={stats.sent} valueStyle={{ color: "#52c41a" }} />
                    </Col>
                    <Col span={6}>
                        <Statistic title="Thất bại" value={stats.failed} valueStyle={{ color: "#ff4d4f" }} />
                    </Col>
                    <Col span={6}>
                        <Statistic title="Đang chờ" value={stats.queued} valueStyle={{ color: "#fa8c16" }} />
                    </Col>
                </Row>
                {hasBeenSent && total > 0 && (
                    <Progress
                        percent={progressPct}
                        success={{ percent: Math.round((stats.sent / total) * 100) }}
                        format={() => `${done.toLocaleString()} / ${total.toLocaleString()}`}
                    />
                )}
            </Card>

            {/* Chi tiết chiến dịch */}
            <Card title="Thông tin chiến dịch" className="mb-4">
                <Descriptions column={1} bordered size="small">
                    <Descriptions.Item label="Tiêu đề email">{campaign.subject}</Descriptions.Item>
                    <Descriptions.Item label="Nội dung">
                        {campaign.templateName ? `Template: ${campaign.templateName}` : "HTML tùy chỉnh"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Đối tượng">
                        <AudienceLabel payload={campaign.audiencePayload} />
                    </Descriptions.Item>
                    <Descriptions.Item label="Tạo bởi">{campaign.createdBy || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Tạo lúc">
                        {campaign.createdAt
                            ? format(new Date(Number(campaign.createdAt)), "dd/MM/yyyy HH:mm:ss")
                            : "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Bắt đầu gửi">
                        {campaign.sentAt
                            ? format(new Date(Number(campaign.sentAt)), "dd/MM/yyyy HH:mm:ss")
                            : "-"}
                    </Descriptions.Item>
                </Descriptions>
            </Card>

            {/* Gửi thử */}
            <Card title="Gửi thử" className="mb-4">
                <Space.Compact style={{ width: "100%" }}>
                    <Input
                        placeholder="email@example.com"
                        value={testEmail}
                        onChange={(e) => setTestEmail(e.target.value)}
                        style={{ maxWidth: 280 }}
                    />
                    <Button onClick={handleTestSend} loading={testLoading}>
                        Gửi thử
                    </Button>
                </Space.Compact>
            </Card>

            {/* Danh sách người nhận */}
            {hasBeenSent && (
                <Card title="Danh sách người nhận" className="mb-4">
                    <EmailCampaignLogsTable campaignId={id} />
                </Card>
            )}

            {/* Email content — rendered preview + source HTML */}
            {campaign.htmlBody && (
                <Card
                    title="Nội dung email"
                    tabList={[{ key: "preview", tab: "Preview" }, { key: "source", tab: "HTML" }]}
                    defaultActiveTabKey="preview"
                    activeTabKey={previewTab}
                    onTabChange={setPreviewTab}
                    extra={campaign.status === "draft" && (
                        <Button
                            size="small"
                            icon={<FontAwesomeIcon icon={faPen} className="mr-1" />}
                            onClick={() => navigate(`/email/${id}/edit`)}
                        >
                            Sửa nội dung
                        </Button>
                    )}
                >
                    {previewTab === "preview" ? (
                        <iframe
                            srcDoc={campaign.htmlBody}
                            title="Email preview"
                            style={{ width: "100%", minHeight: 320, border: "none", display: "block" }}
                            sandbox="allow-same-origin"
                        />
                    ) : (
                        <pre className="text-xs overflow-auto bg-surface-sunken p-3 rounded" style={{ maxHeight: 320 }}>
                            {campaign.htmlBody}
                        </pre>
                    )}
                </Card>
            )}
        </div>
    );
}

export default EmailCampaignDetail;
